import axios from 'axios'
import { apiClient } from '@/api/client'
import { fetchReservations, updateReservationStatus } from '@/features/reservations/services/reservationsApi'
import type { GuestReceipt, VoucherReceipt } from './guestReceipt'
import type { FloorPlan, PlanRoom } from './floorPlan'
import type { DraftContent, DraftSummary, MenuContext, MenuItemDetail, OrderDraft, OrderQuote, PosMenu, PosOrderRequest, SubmittedOrder, PosPaymentInput, PosPayment, PosPaymentSummary, PosRefundInput, PosRefund, PosRefundSummary, PosRefundOutcome, PosCashStatus, PosCashOperation, PosCashClosing, PosCashInterim, OpenOrder, PosCustomer, PosStaffAction, PosStaffEvent, PosStaffLockMode, PosStaffLogin, PosStaffMember, PosStaffRole, PosStaffStatus, PosSplitInput, PosStaffReport, PosVoid, PosVoidInput, PosVoucher } from './types'

const root = (restaurantId: number) => `/api/restaurants/${restaurantId}`
const draftPath = (restaurantId: number, id: string) => `${root(restaurantId)}/order-drafts/${encodeURIComponent(id)}`
const posContent = (content: DraftContent) => ({ ...content, order: { ...content.order, source: 'pos' as const } })

export async function fetchPosMenu(restaurantId: number, context: MenuContext = {}, signal?: AbortSignal) {
  return (await apiClient.get<PosMenu>(`${root(restaurantId)}/pos-menu`, { params: context, signal })).data
}
export async function fetchPosMenuItem(restaurantId: number, itemId: number, context: MenuContext = {}, signal?: AbortSignal) {
  return (await apiClient.get<PosMenu<MenuItemDetail>>(`${root(restaurantId)}/pos-menu/items/${itemId}`, { params: context, signal })).data
}
/** Sold out or available again for every channel (webshop, apps, POS). */
export async function setDishAvailable(restaurantId: number, itemId: number, available: boolean) {
  await apiClient.put(`${root(restaurantId)}/pos-menu/items/${itemId}/availability`, { available })
}
export async function quotePosOrder(restaurantId: number, order: PosOrderRequest, signal?: AbortSignal) {
  return (await apiClient.post<OrderQuote>(`${root(restaurantId)}/order-quotes`, { ...order, source: 'pos' }, { signal })).data
}

/** Full POS draft quote, including delivery rules and fees. Existing item-only quote stays available. */
export async function quotePosDraft(restaurantId: number, content: DraftContent, signal?: AbortSignal) {
  return (await apiClient.post<OrderQuote>(`${root(restaurantId)}/order-drafts/quote`, { content: posContent(content) }, { signal })).data
}

/** Prepare once per new draft. Retry this same request, even if the editor has since changed. */
export function prepareDraftCreation(content: DraftContent) {
  return { content: structuredClone(posContent(content)), idempotencyKey: crypto.randomUUID() }
}
export async function createOrderDraft(restaurantId: number, request: ReturnType<typeof prepareDraftCreation>, signal?: AbortSignal) {
  return (await apiClient.post<OrderDraft>(`${root(restaurantId)}/order-drafts`, request, { signal })).data
}
export async function fetchOrderDraft(restaurantId: number, id: string, signal?: AbortSignal) {
  return (await apiClient.get<OrderDraft>(draftPath(restaurantId, id), { signal })).data
}
export async function fetchOrderDrafts(restaurantId: number, limit = 50, offset = 0, signal?: AbortSignal) {
  return (await apiClient.get<{ drafts: DraftSummary[] }>(`${root(restaurantId)}/order-drafts`, { params: { limit, offset }, signal })).data.drafts
}
export async function updateOrderDraft(restaurantId: number, id: string, revision: number, content: DraftContent, acceptMenuChanges = false, signal?: AbortSignal) {
  return (await apiClient.put<OrderDraft>(draftPath(restaurantId, id), { revision, content: posContent(content), acceptMenuChanges }, { signal })).data
}
export async function fetchGuestReceipt(restaurantId: number, orderIds: number[], signal?: AbortSignal) {
  return (await apiClient.get<GuestReceipt>(`${root(restaurantId)}/pos-receipt`, { params: { orderIds: orderIds.join(',') }, signal })).data
}
export async function cancelPosOrder(restaurantId: number, orderId: number, cancelReason: string) {
  await apiClient.post(`/api/admin/restaurants/${restaurantId}/orders/${orderId}/cancel`, { cancelReason })
}
export async function voidOrderLine(restaurantId: number, orderId: number, input: PosVoidInput) {
  return (await apiClient.post<PosVoid>(`${root(restaurantId)}/pos-orders/${orderId}/voids`, input)).data
}
export async function discardOrderDraft(restaurantId: number, id: string, revision: number, signal?: AbortSignal) {
  await apiClient.delete(draftPath(restaurantId, id), { params: { revision }, signal })
}
/** Retry with the same revision after an uncertain response; the backend returns the same order. */
export async function submitOrderDraft(restaurantId: number, id: string, revision: number, signal?: AbortSignal) {
  return (await apiClient.post<SubmittedOrder>(`${draftPath(restaurantId, id)}/submit`, { revision }, { signal })).data
}
/** Atomic order acceptance and tender recording. Never invokes or retries a card charge. */
export async function checkoutOrderDraft(restaurantId: number, id: string, revision: number, payment: PosPaymentInput) {
  return (await apiClient.post<SubmittedOrder>(`${draftPath(restaurantId, id)}/checkout`, { revision, payment })).data
}
export async function fetchPosPayment(restaurantId: number, orderId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosPaymentSummary>(`${root(restaurantId)}/pos-orders/${orderId}/payment`, { signal })).data
}
export async function collectPosPayment(restaurantId: number, orderId: number, input: PosPaymentInput) {
  return (await apiClient.post<PosPayment>(`${root(restaurantId)}/pos-orders/${orderId}/payment`, input)).data
}
/** Earlier POS delivery orders for a phone number, newest first, one per address. */
export async function fetchPosCustomers(restaurantId: number, phone: string, signal?: AbortSignal) {
  return (await apiClient.post<{ customers: PosCustomer[] }>(`${root(restaurantId)}/pos-customers`, { phone }, { signal })).data.customers
}
/** Orders sent to the kitchen without payment during the last 24 hours, oldest first. */
export async function fetchOpenOrders(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<{ orders: OpenOrder[] }>(`${root(restaurantId)}/pos-open-orders`, { signal })).data.orders
}
export async function fetchPosPayments(restaurantId: number, orderIds: number[], signal?: AbortSignal) {
  return (await apiClient.get<{ payments: PosPayment[] }>(`${root(restaurantId)}/pos-payments`, {
    params: { orderIds: orderIds.join(',') }, signal,
  })).data.payments
}

/** Keep cancellation separate from API/transport failures. No automatic write retries. */
export function posOrderingError(error: unknown): { code: string; status?: number; itemIndex?: number; cancelled: boolean } {
  if (axios.isCancel(error)) return { code: 'request_cancelled', cancelled: true }
  if (axios.isAxiosError<{ error?: string; itemIndex?: number | null } | null | undefined>(error)) {
    return { code: error.response?.data?.error ?? 'request_failed', status: error.response?.status,
      itemIndex: error.response?.data?.itemIndex ?? undefined, cancelled: false }
  }
  return { code: 'request_failed', cancelled: false }
}

export async function fetchPosRefunds(restaurantId: number, orderId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosRefundSummary>(`${root(restaurantId)}/pos-orders/${orderId}/refunds`, { signal })).data
}
export async function fetchRefundReceipt(restaurantId: number, orderId: number, refundId: string, signal?: AbortSignal) {
  return (await apiClient.get<GuestReceipt>(`${root(restaurantId)}/pos-orders/${orderId}/refunds/${encodeURIComponent(refundId)}/receipt`, { signal })).data
}
export async function preparePosRefund(restaurantId: number, orderId: number, input: PosRefundInput) {
  return (await apiClient.post<PosRefund>(`${root(restaurantId)}/pos-orders/${orderId}/refunds`, input)).data
}
export async function finishPosRefund(restaurantId: number, orderId: number, id: string, input: PosRefundOutcome) {
  return (await apiClient.post<PosRefund>(`${root(restaurantId)}/pos-orders/${orderId}/refunds/${encodeURIComponent(id)}/outcome`, input)).data
}
export async function cancelRefundedPosOrder(restaurantId: number, orderId: number) {
  return (await apiClient.post<{ cancelled: boolean }>(`${root(restaurantId)}/pos-orders/${orderId}/cancel-refunded`, {})).data
}

export async function fetchPosCash(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosCashStatus>(`${root(restaurantId)}/pos-cash`, { signal })).data
}
export async function executePosCash(restaurantId: number, input: PosCashOperation) {
  return (await apiClient.post<{ sessionId: string; kind: string; report?: PosCashInterim }>(`${root(restaurantId)}/pos-cash/operations`, input)).data
}
export async function fetchPosCashReport(restaurantId: number, sessionId: string, signal?: AbortSignal) {
  return (await apiClient.get<PosCashClosing>(`${root(restaurantId)}/pos-cash/sessions/${encodeURIComponent(sessionId)}`, { signal })).data
}

const staffPath = (restaurantId: number) => `${root(restaurantId)}/pos-staff`
export async function fetchStaffStatus(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosStaffStatus>(staffPath(restaurantId), { signal })).data
}
export async function setupStaff(restaurantId: number, name: string, pin: string, accessKey: string) {
  return (await apiClient.post<PosStaffLogin>(`${staffPath(restaurantId)}/setup`, { name, pin, accessKey })).data
}
export async function recoverStaffPin(restaurantId: number, staffId: string, pin: string, accessKey: string) {
  await apiClient.post(`${staffPath(restaurantId)}/recover`, { staffId, pin, accessKey })
}
export async function loginStaff(restaurantId: number, staffId: string, pin: string) {
  return (await apiClient.post<PosStaffLogin>(`${staffPath(restaurantId)}/login`, { staffId, pin })).data
}
export async function logoutStaff(restaurantId: number) {
  await apiClient.post(`${staffPath(restaurantId)}/logout`)
}
export async function approveStaffAction(restaurantId: number, action: PosStaffAction, staffId: string, pin: string) {
  return (await apiClient.post<{ token: string }>(`${staffPath(restaurantId)}/approvals`, { action, staffId, pin })).data.token
}
export async function fetchStaffMembers(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<{ staff: PosStaffMember[] }>(`${staffPath(restaurantId)}/members`, { signal })).data.staff
}
export async function createStaffMember(restaurantId: number, input: { name: string; role: PosStaffRole; pin: string }) {
  return (await apiClient.post<PosStaffMember>(`${staffPath(restaurantId)}/members`, input)).data
}
export async function updateStaffMember(restaurantId: number, id: string, input: { name: string; role: PosStaffRole; active: boolean; pin: string | null }) {
  return (await apiClient.put<PosStaffMember>(`${staffPath(restaurantId)}/members/${encodeURIComponent(id)}`, input)).data
}
export async function saveStaffSettings(restaurantId: number, input: { enabled: boolean; lockMode: PosStaffLockMode; approvals: PosStaffAction[] }) {
  await apiClient.put(`${staffPath(restaurantId)}/settings`, input)
}
export async function fetchStaffLog(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<{ events: PosStaffEvent[] }>(`${staffPath(restaurantId)}/log`, { signal })).data.events
}

export async function fetchFloorPlan(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<FloorPlan>(`${root(restaurantId)}/pos-floor-plan`, { signal })).data
}
export async function saveFloorPlan(restaurantId: number, revision: number, rooms: PlanRoom[]) {
  return (await apiClient.put<FloorPlan>(`${root(restaurantId)}/pos-floor-plan`, { revision, rooms })).data
}

export async function fetchVoucher(restaurantId: number, code: string, signal?: AbortSignal) {
  return (await apiClient.get<PosVoucher>(`${root(restaurantId)}/pos-vouchers/${encodeURIComponent(code)}`, { signal })).data
}
export async function sellVoucher(restaurantId: number, input: PosPaymentInput) {
  return (await apiClient.post<PosVoucher>(`${root(restaurantId)}/pos-vouchers`, input)).data
}
export async function fetchVoucherReceipt(restaurantId: number, id: string, signal?: AbortSignal) {
  return (await apiClient.get<VoucherReceipt>(`${root(restaurantId)}/pos-vouchers/${encodeURIComponent(id)}/receipt`, { signal })).data
}

export async function splitOrders(restaurantId: number, input: PosSplitInput) {
  return (await apiClient.post<{ orderIds: number[] }>(`${root(restaurantId)}/pos-order-splits`, input)).data.orderIds
}
export async function fetchStaffReport(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosStaffReport>(`${root(restaurantId)}/pos-cash/staff`, { signal })).data
}

/** Ends today's seated reservation of a table, so the plan shows it free again. */
export async function completeSeatedReservation(restaurantId: number, table: string) {
  const today = new Date().toLocaleDateString('sv')
  const { reservations } = await fetchReservations(restaurantId, today, today, 'SEATED')
  await Promise.all(reservations.filter(r => r.tableLabel === table).map(r => updateReservationStatus(restaurantId, r.id, 'COMPLETED', r.version)))
}
